import clsx from 'clsx'
import { useState, ChangeEvent, MouseEvent, FormEvent, useEffect } from "react"
import {
    Button,
    FormControl,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    makeStyles,
    CircularProgress,
} from "@material-ui/core"
import { Visibility, VisibilityOff } from "@material-ui/icons"
import { ActionAlert } from "../"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { clearErrorMessage, validateHireDateApiCall } from "../../redux/reducers/newHire"
import { McK_ELECTRIC_BLUE } from '../../styles/colors'
import { useTranslation } from "react-i18next";

interface State {
    hireDate: string
    showAssetTag: boolean
}

const useStyles = makeStyles({
    form: {
        display: 'flex',
        flexDirection: 'column',
    },
    primaryButton: {
        marginTop: '20px',
        width: '150px',
        alignSelf: 'center',
    },
    submitButton: {
        textTransform: 'capitalize',
        backgroundColor: McK_ELECTRIC_BLUE,
    },
    textField: {
        width: '290px'
    }
})

const HiredateValidationForm = () => {
    const classes = useStyles()
    const {t} = useTranslation(['common', 'errors']);
    const dispatch = useAppDispatch()
    const { verifyDetails, userID, isLoading, errorMessage, errorCode } = useAppSelector(state => state.newHire)
    const translatedError = errorCode
        ? t(`errors:codes.${errorCode}`, {
            defaultValue: errorMessage || t('errors:codes.INTERNAL_SERVER_ERROR'),
        })
        : (errorMessage || t('errors:codes.INTERNAL_SERVER_ERROR'))

    useEffect(() => {
            if (errorMessage) {
                const timer = setTimeout(() => {
                    dispatch(clearErrorMessage());
                }, 5000); // 5 seconds
                return () => clearTimeout(timer);
            }
        }, [errorMessage, dispatch]);

    const [values, setValues] = useState<State>({
        hireDate: '',
        showAssetTag: true,
    })

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {

        const isoDateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
        
        // Check if the date object is valid
        const dateObj = new Date(values.hireDate);
            if (isNaN(dateObj.getTime())) {
               
                setValues(prevValues => ({
                    ...prevValues,
                    hireDate: "0001-01-01T00:00:01Z" // Set to default value if invalid
                })
                );
            }     

        //if hire date is not an empty string and does not match the ISO date time format, append "T00:00:00Z" to it
        if (values.hireDate !== "" && !isoDateTimeRegex.test(values.hireDate)) {
           
            
           
            values.hireDate = `${values.hireDate}T00:00:00Z`;
        }
       

        event.preventDefault()

        // API call to verify hiredate
        dispatch(validateHireDateApiCall(verifyDetails.verifyID, values.hireDate, userID, verifyDetails.isInternal))

        setValues({ ...values, hireDate: '' }) // reset the hire date value in state
    }

    const handleClickShowPassword = () => {
        setValues({ ...values, showAssetTag: !values.showAssetTag })
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {

        // Basic ISO format check. It limits the Input type date element to limit the input to YYYY-MM-DD format

        let date = event.target.value;
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!isoDateRegex.test(date)) {
            
            return;
  }

        setValues({ ...values, hireDate: event.target.value })
    }

    const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <div>
            <form onSubmit={handleSubmit} className={classes.form} autoComplete="off">
                <FormControl className={classes.textField}>
                    <InputLabel htmlFor="standard-adornment-password" shrink >{t('get_hire_date.field')}</InputLabel>
                    <Input
                        id="standard-adornment-password"
                        type={values.showAssetTag ? 'date' : 'password'}
                        value={values.hireDate}
                        onChange={handleChange}
                        autoComplete="off"
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton 
                                    style={{ visibility: values.showAssetTag ? 'hidden' : 'visible'}}
                                    aria-label="toggle serial number visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    
                                >
                                    {values.showAssetTag ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
                {
                    isLoading ?
                        <CircularProgress className={classes.primaryButton} />
                        :
                        <Button disabled={!values.hireDate} // disable button if hire date is empty
                            type="submit"
                            variant="contained"
                            color="primary"
                            className={clsx(classes.primaryButton, classes.submitButton)}
                        >
                            {t('get_hire_date.submit')}
                        </Button>
                }
                {
                    // show action alert only if error is not null
                    errorMessage !== null &&
                    <ActionAlert
                        isError={errorMessage !== null} // value is true if error is NOT null
                        message={translatedError} // error message
                        isOpen={errorMessage !== null} // value is true if error is NOT null
                        onClose={() => {
                            dispatch(clearErrorMessage()) // on close button click set errorMessage to null
                        }}
                    />
                }
            </form>
        </div>
    )
}

export default HiredateValidationForm